import React, { useEffect, useState, useRef } from "react";
import { MdEdit, MdDelete, MdAdd, MdFileDownload } from "react-icons/md";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import { formatDate, formatTime, today } from "../../Helper/Helper";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../http";
import Spinner from "../Spinner";
import jsPDF from "jspdf";
import "jspdf-autotable";

const activeTabStyles = `
  .btn-group .btn.active-view {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .btn-group .btn.active-unit {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .btn-group .btn.active-measurement {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .sort-dropdown {
    min-width: 200px;
  }
  
  .action-buttons {
    padding: 0.25rem 0.5rem;
  }
  
  .input-group-unit {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  
  .input-group-unit .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  .input-group-unit .input-group-append {
    margin-left: -1px;
  }
  
  .input-group-unit .input-group-text {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

function BloodGlucoseList() {
  const patientData = useSelector((store) => store.patient.patientLogin);
  const [isLoading, setIsLoading] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [filterData, setFilterData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [editData, setEditData] = useState({});
  const [addData, setAddData] = useState({
    measurement: "",
    measurement_type: "Fasting",
    record_date: "",
    record_time: "",
    notes: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [glucoseUnit, setGlucoseUnit] = useState("mg/dL"); // 'mg/dL' or 'mmol/L'
  const [inputUnit, setInputUnit] = useState("mg/dL"); // Unit for input field
  const [sortOption, setSortOption] = useState({ value: 'record_date-desc', label: 'Date (Newest First)' });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedMeasurementType, setSelectedMeasurementType] = useState({ value: "All", label: "All" });

  // Add custom CSS for active tabs
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = activeTabStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [glucoseUnit, selectedMeasurementType]);

  const measurementTypeOptions = [
    { value: "All", label: "Types of Measurement" },
    { value: "After Meal", label: "After Meal" },
    { value: "Fasting", label: "Fasting" },
    { value: "Random", label: "Random" },
  ];

  const unitOptions = [
    { value: "mg/dL", label: "mg/dL" },
    { value: "mmol/L", label: "mmol/L" },
  ];

  const sortOptions = [
    { value: "record_date-desc", label: "Date (Newest First)" },
    { value: "record_date-asc", label: "Date (Oldest First)" },
    { value: "measurement-desc", label: "Glucose Level (High to Low)" },
    { value: "measurement-asc", label: "Glucose Level (Low to High)" },
  ];

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setAddData({
      measurement: "",
      measurement_type: "Fasting",
      record_date: "",
      record_time: "",
      notes: "",
    });
    setInputUnit("mg/dL");
    setValidationErrors({});
  };

  const fetchData = async (filter) => {
    setIsLoading(true);
    try {
      const response = await SendPostRequest("healthcare/blood_glucose/grid", {
        logged_id: patientData?.logged_id,
        filter: filter || {},
        measurement_type:
          selectedMeasurementType.value === "All"
            ? ""
            : selectedMeasurementType.value,
        unit: glucoseUnit,
      });
      if (response?.status === "success") {
        const data = response?.data || [];
        setGetData(data);
        // Apply sorting after fetching data
        handleSortChange(sortOption, data);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (option, dataToSort = getData) => {
    const [key, direction] = option.value.split("-");

    const sortedData = [...dataToSort].sort((a, b) => {
      // Handle different data types for sorting
      if (key === "record_date") {
        const dateA = new Date(a[key]);
        const dateB = new Date(b[key]);
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      } else if (key === "measurement") {
        // Convert to numbers for proper numerical comparison
        const valueA = parseFloat(a[key]);
        const valueB = parseFloat(b[key]);
        return direction === "asc" ? valueA - valueB : valueB - valueA;
      } else {
        if (a[key] < b[key]) {
          return direction === "asc" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "asc" ? 1 : -1;
        }
        return 0;
      }
    });

    setGetData(sortedData);
    setSortOption(option);
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Prevent negative values for measurement field
    if (name === "measurement" && value.includes("-")) {
      return;
    }

    // Validate inputs
    let errors = { ...validationErrors };

    if (name === "measurement") {
      // Check for decimal places based on input unit
      if (inputUnit === "mmol/L") {
        // For mmol/L, allow only one decimal place
        const decimalCount = (value.split(".")[1] || "").length;
        if (decimalCount > 1) {
          errors[name] = "You can only enter up to one decimal place";
        } else if (!/^\d*\.?\d*$/.test(value)) {
          errors[name] = "Please enter a valid number";
        } else {
          const numValue = parseFloat(value);
          if (numValue < 1) {
            errors[name] = "Glucose must be at least 1 mmol/L";
          } else if (numValue > 150) {
            errors[name] = "Glucose must be less than 150 mmol/L";
          } else {
            delete errors[name];
          }
        }
      } else {
        // For mg/dL, don't allow decimals
        if (!/^\d*$/.test(value)) {
          errors[name] = "Please enter a whole number";
        } else {
          const numValue = parseInt(value);
          if (numValue < 20) {
            errors[name] = "Glucose must be at least 20 mg/dL";
          } else if (numValue > 2500) {
            errors[name] = "Glucose must be less than 2500 mg/dL";
          } else {
            delete errors[name];
          }
        }
      }
    }

    if (name === "record_time") {
      // Check if time is in the future
      const currentTime = new Date().toTimeString().slice(0, 5);
      if (
        addData.record_date === new Date().toISOString().split("T")[0] &&
        value > currentTime
      ) {
        errors[name] = "Future times are not allowed";
      } else {
        delete errors[name];
      }
    }

    setValidationErrors(errors);

    if (editData.id) {
      setEditData((prv) => ({ ...prv, [name]: value }));
    } else {
      setAddData((prv) => ({ ...prv, [name]: value }));
    }
  };

  const handleInputUnitChange = (selectedOption) => {
    const newUnit = selectedOption.value;

    // Convert the value if needed
    if (addData.measurement || editData.measurement) {
      let convertedValue = editData.id
        ? editData.measurement
        : addData.measurement;

      if (inputUnit === "mg/dL" && newUnit === "mmol/L") {
        // Convert mg/dL to mmol/L
        convertedValue = (parseFloat(convertedValue) / 18).toFixed(1);
      } else if (inputUnit === "mmol/L" && newUnit === "mg/dL") {
        // Convert mmol/L to mg/dL
        convertedValue = Math.round(parseFloat(convertedValue) * 18);
      }

      if (editData.id) {
        setEditData((prev) => ({ ...prev, measurement: convertedValue }));
      } else {
        setAddData((prev) => ({ ...prev, measurement: convertedValue }));
      }
    }

    setInputUnit(newUnit);

    // Clear any existing measurement error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.measurement;
      return newErrors;
    });
  };

  const handleSelectChange = (e) => {
    if (editData.id) {
      setEditData((prv) => ({ ...prv, measurement_type: e.value }));
    } else {
      setAddData((prv) => ({ ...prv, measurement_type: e.value }));
    }
  };

  const validateForm = (formData) => {
    const errors = {};

    if (!formData.measurement || formData.measurement <= 0) {
      errors.measurement = "Please enter a valid glucose level";
    } else {
      const glucoseValue = parseFloat(formData.measurement);

      if (inputUnit === "mg/dL") {
        if (glucoseValue < 20) {
          errors.measurement = "Glucose must be at least 20 mg/dL";
        } else if (glucoseValue > 2500) {
          errors.measurement = "Glucose must be less than 2500 mg/dL";
        }
      } else {
        if (glucoseValue < 1) {
          errors.measurement = "Glucose must be at least 1 mmol/L";
        } else if (glucoseValue > 150) {
          errors.measurement = "Glucose must be less than 150 mmol/L";
        }

        // Check decimal places for mmol/L
        const decimalCount = (
          formData.measurement.toString().split(".")[1] || ""
        ).length;
        if (decimalCount > 1) {
          errors.measurement = "You can only enter up to one decimal place";
        }
      }
    }

    if (!formData.record_date) {
      errors.record_date = "Date is required";
    }

    if (!formData.record_time) {
      errors.record_time = "Time is required";
    }

    // Check if date is in the future
    if (formData.record_date && new Date(formData.record_date) > new Date()) {
      errors.record_date = "Future dates are not allowed";
    }

    // Check if time is in the future for today's date
    if (
      formData.record_date === new Date().toISOString().split("T")[0] &&
      formData.record_time
    ) {
      const currentTime = new Date().toTimeString().slice(0, 5);
      if (formData.record_time > currentTime) {
        errors.record_time = "Future times are not allowed";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm(editData)) {
      return;
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      record_id: editData?.id,
      form_data: {
        measurement: editData.measurement,
        measurement_type: editData.measurement_type,
        record_date: editData?.record_date,
        record_time: editData?.record_time,
        notes: editData?.notes,
      },
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/edit",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        handleModalClose();
        fetchData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to update record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(addData)) {
      return;
    }

    // Convert to the system unit if needed
    let measurementValue = addData.measurement;
    if (inputUnit !== glucoseUnit) {
      if (inputUnit === "mg/dL" && glucoseUnit === "mmol/L") {
        measurementValue = (parseFloat(addData.measurement) / 18).toFixed(1);
      } else if (inputUnit === "mmol/L" && glucoseUnit === "mg/dL") {
        measurementValue = Math.round(parseFloat(addData.measurement) * 18);
      }
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      form_data: {
        measurement: measurementValue,
        measurement_type: addData.measurement_type,
        record_date: addData.record_date,
        record_time: addData.record_time,
        notes: addData.notes,
      },
    };

    setIsLoading(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/add",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success("Record has been added successfully");
        handleModalClose();
        setAddData({
          measurement: "",
          measurement_type: "Fasting",
          record_date: "",
          record_time: "",
          notes: "",
        });
        setInputUnit("mg/dL");
        fetchData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to add record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const data = {
      logged_id: patientData?.logged_id,
      record_id: deleteId,
    };

    setLoadingDelete(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/delete",
        data
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        fetchData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    if (!filterData?.date_from || !filterData?.date_to) {
      toast.error("Please select both from and to dates");
      return;
    }

    if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
      toast.error("From date cannot be after to date");
      return;
    }

    fetchData(filterData);
  };

  const calculateStats = () => {
    if (getData.length === 0) return { min: 0, max: 0, avg: 0 };

    const values = getData.map((item) => parseFloat(item.measurement));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

    return {
      min: min.toFixed(inputUnit === "mmol/L" ? 1 : 0),
      max: max.toFixed(inputUnit === "mmol/L" ? 1 : 0),
      avg: avg.toFixed(inputUnit === "mmol/L" ? 1 : 0),
    };
  };

  const exportToCSV = () => {
    const stats = calculateStats();

    const title = "Blood Glucose Records";

    // Date Generated
    const dateGenerated = `Generated On: ${formatDate(today)}`;
    const emptyLine = "";
    const headers = [
      "Sr.No",
      "Record Date",
      "Record Time",
      "Glucose Level",
      "Type",
      "Note",
      "Status",
    ];
    const csvData = getData.map((item, index) => [
      index + 1,
      formatDate(item.record_date),
      formatTime(item.record_time),
      item.measurement,
      item.measurement_type,
      item.notes || "",
      item.status || "",
    ]);

    // Summary information
    const summary = [
      // ["Date Generated", today],
      ["Total Records", getData.length],
      ["Unit", glucoseUnit],
      ["Measurement Type", selectedMeasurementType.label],
      ["Minimum", `${stats.min} ${glucoseUnit}`],
      ["Maximum", `${stats.max} ${glucoseUnit}`],
      ["Average", `${stats.avg} ${glucoseUnit}`],
    ];

    // CSV content: table first, then summary
    const csvContent = [
      title,
      dateGenerated,
      emptyLine,
      headers.join(","), // table headers
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(",")), // table rows
      "", // blank line before summary
      "Blood Glucose Records Summary", // summary title
      ...summary.map((row) => row.join(": ")), // summary rows
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `blood_glucose_${today}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const stats = calculateStats();
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text("Blood Glucose Records", 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

    // Table
    const tableYStart = 40;
    doc.autoTable({
      startY: tableYStart,
      head: [
        [
          "Sr.No",
          "Date",
          "Time",
          `Glucose (${glucoseUnit})`,
          "Type",
          "Notes",
          "Status",
        ],
      ],
      body: getData.map((item, index) => [
        index + 1,
        formatDate(item.record_date),
        formatTime(item.record_time),
        item.measurement,
        item.measurement_type,
        item.notes || "-",
        item.status || "-",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [66, 139, 202],
      },
      columnStyles: {
        0: { cellWidth: 12 }, // Sr.No
        1: { cellWidth: 25 }, // Date
        2: { cellWidth: 20 }, // Time
        3: { cellWidth: 30 }, // Glucose
        4: { cellWidth: 25 }, // Type
        5: { cellWidth: 60 }, // Notes
        6: { cellWidth: 20 }, // Status
      },
      styles: { fontSize: 10 },
    });

    // Summary information after the table
    let finalY = doc.lastAutoTable.finalY + 10; // start below table
    // doc.setFontSize(11);
    // doc.text(`Date Generated: ${today}`, 14, finalY);
    finalY += 6;
    doc.text(`Total Records: ${getData.length}`, 14, finalY);
    finalY += 6;
    doc.text(`Unit: ${glucoseUnit}`, 14, finalY);
    finalY += 6;
    doc.text(`Measurement Type: ${selectedMeasurementType.label}`, 14, finalY);
    finalY += 6;
    doc.text(`Minimum: ${stats.min} ${glucoseUnit}`, 14, finalY);
    finalY += 6;
    doc.text(`Maximum: ${stats.max} ${glucoseUnit}`, 14, finalY);
    finalY += 6;
    doc.text(`Average: ${stats.avg} ${glucoseUnit}`, 14, finalY);

    doc.save(`blood_glucose_records_${today}.pdf`);
  };

  const getGlucoseStatus = (glucose, type) => {
    let status;
    const glucoseValue = parseFloat(glucose);

    if (glucoseUnit === "mg/dL") {
      if (type === "Fasting") {
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 100) status = "normal";
        else if (glucoseValue > 100 && glucoseValue <= 125) status = "elevated";
        else status = "high";
      } else if (type === "After Meal") {
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 140) status = "normal";
        else if (glucoseValue > 140 && glucoseValue <= 200) status = "elevated";
        else status = "high";
      } else {
        // Random
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 140) status = "normal";
        else if (glucoseValue > 140 && glucoseValue <= 200) status = "elevated";
        else status = "high";
      }
    } else {
      // mmol/L
      if (type === "Fasting") {
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 5.6) status = "normal";
        else if (glucoseValue > 5.6 && glucoseValue <= 6.9) status = "elevated";
        else status = "high";
      } else if (type === "After Meal") {
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 7.8) status = "normal";
        else if (glucoseValue > 7.8 && glucoseValue <= 11.1)
          status = "elevated";
        else status = "high";
      } else {
        // Random
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 7.8) status = "normal";
        else if (glucoseValue > 7.8 && glucoseValue <= 11.1)
          status = "elevated";
        else status = "high";
      }
    }

    return status;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "low":
        return "info";
      case "normal":
        return "success";
      case "elevated":
        return "warning";
      case "high":
        return "danger";
      default:
        return "secondary";
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="Prescripition">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <h3>Blood Glucose</h3>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-default d-flex"
              style={{ alignItems: "center" }}
              onClick={handleModalOpen}
            >
              <MdAdd className="me-1" /> Add Record
            </button>
            <div className="btn-group border-none-csv-pdf">
              <button
                className="btn btn-default d-flex"
                style={{ alignItems: "center" }}
                onClick={exportToCSV}
                disabled={getData.length === 0}
              >
                <MdFileDownload className="me-1" /> CSV
              </button>
              <button
                className="btn btn-default d-flex"
                style={{ alignItems: "center" }}
                onClick={exportToPDF}
                disabled={getData.length === 0}
              >
                <MdFileDownload className="me-1" /> PDF
              </button>
            </div>
            <div className="btn-group" role="group">
              <button
                type="button "
                className={`chart-views-btn ${
                  glucoseUnit === "mg/dL"
                    ? "chart-active-views-btn"
                    : "chart-views-btn"
                }`}
                onClick={() => setGlucoseUnit("mg/dL")}
              >
                mg/dL
              </button>
              <button
                type="button "
                className={`chart-views-btn-second ${
                  glucoseUnit === "mmol/L"
                    ? "chart-active-views-btn"
                    : "chart-views-btn-second"
                }`}
                onClick={() => setGlucoseUnit("mmol/L")}
              >
                mmol/L
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div
            style={{ marginBottom: "60px" }}
            className="d-flex align-items-center justify-content-center "
          >
            <Spinner />
          </div>
        ) : (
          <div className="table-responsive weight-managment-carda">
            {/* Filter + type/sort row (aligned like Weight Management list) */}
            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
              <form
                onSubmit={handleFilter}
                className="d-flex flex-wrap align-items-end gap-3"
              >
                <div className="form-group mb-0">
                  <label htmlFor="from" className="form-label mb-0">
                    From:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_from"
                    className="form-control"
                    value={filterData?.date_from || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "auto", minWidth: 150 }}
                  />
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="to" className="form-label mb-0">
                    To:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_to"
                    className="form-control"
                    value={filterData?.date_to || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "auto", minWidth: 150 }}
                  />
                </div>
                <button type="submit" className="btn btn-default">
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setFilterData(null);
                    fetchData(null);
                  }}
                >
                  Clear
                </button>
              </form>
              <div className="d-flex align-items-end ms-auto mt-2 mt-md-0">
                <div className="d-flex align-items-center ms-2">
                  <div className="me-2">
                    <Select
                      className="filter-dropdown-blood-glucose-list"
                      value={selectedMeasurementType}
                      options={measurementTypeOptions}
                      onChange={setSelectedMeasurementType}
                      isSearchable={false}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minWidth: "160px",
                        }),
                      }}
                    />
                  </div>
                  <div className="me-2">
                    <Select
                      className="filter-dropdown-blood-glucose"
                      value={sortOption}
                      options={sortOptions}
                      onChange={(option) => handleSortChange(option)}
                      isSearchable={false}
                      placeholder="Sort by..."
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minWidth: "160px",
                          height: "40px",
                        }),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table mt-2">
                <thead className="thead-light">
                  <tr>
                    <th
                      scope="col"
                      style={{ width: "10%", textAlign: "center" }}
                    >
                      SR.No
                    </th>
                    <th scope="col">Record Date</th>
                    <th scope="col">Record Time</th>
                    <th scope="col">Glucose Level ({glucoseUnit})</th>
                    <th scope="col">Type</th>
                    {/* <th scope="col" style={{ width: '200px' }}>Note</th> */}
                    <th scope="col">Status</th>
                    <th scope="col" style={{ width: "10%" }}>
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {getData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No blood glucose records found
                      </td>
                    </tr>
                  ) : (
                    getData.map((item, index) => {
                      const statusColor = item?.color || "#000";

                      return (
                        <tr key={item.id}>
                          <td style={{ textAlign: "center" }}>{index + 1}</td>
                          <td>{formatDate(item.record_date)}</td>
                          <td>{formatTime(item.record_time)}</td>
                          <td>{item.measurement}</td>
                          <td>{item.measurement_type}</td>
                          {/* <td style={{ width: '200px', wordWrap: 'break-word' }}>{item.notes || "--"}</td> */}
                          <td>
                            <span
                              className={`badge`}
                              style={{
                                backgroundColor: statusColor,
                                color: "#fff",
                                textTransform: "capitalize",
                                fontSize: "10px",
                              }}
                            >
                              {item.status || "Unknown"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn  btn-default "
                                onClick={() => {
                                  setEditData(item);
                                  handleModalOpen();
                                }}
                                title="Edit"
                              >
                                <MdEdit />
                              </button>
                              <button
                                className="btn btn-default"
                                onClick={() => {
                                  setDeleteId(item.id);
                                  setShowDeleteConfirm(true);
                                }}
                                disabled={LoadingDelete}
                                title="Delete"
                              >
                                {LoadingDelete && deleteId === item.id ? (
                                  <SpinnerBoot size="sm" />
                                ) : (
                                  <MdDelete />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      ;{/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <form onSubmit={editData.id ? handleEdit : handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editData.id
                ? "Edit Blood Glucose Record"
                : "Add Blood Glucose Record"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Glucose Level*</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step={inputUnit === "mmol/L" ? "0.1" : "1"}
                      className={`form-control ${
                        validationErrors.measurement ? "is-invalid" : ""
                      }`}
                      placeholder={inputUnit === "mg/dL" ? "20-2500" : "1-150"}
                      value={
                        editData.id ? editData.measurement : addData.measurement
                      }
                      name="measurement"
                      onChange={handleOnChange}
                      onKeyPress={(e) => {
                        // always prevent '-' and 'e'
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                        // additionally prevent '.' if unit is mg/dL
                        if (inputUnit === "mg/dL" && e.key === ".") {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      max={inputUnit === "mg/dL" ? "2500" : "150"}
                      required
                    />
                    <div className="input-group-append">
                      <Select
                        options={unitOptions}
                        value={unitOptions.find(
                          (opt) => opt.value === inputUnit
                        )}
                        onChange={handleInputUnitChange}
                        isSearchable={false}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minWidth: "100px",
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            height: "100%",
                          }),
                        }}
                      />
                    </div>
                  </div>

                  {validationErrors.measurement && (
                    <div className="invalid-feedback d-block mt-0">
                      {validationErrors.measurement}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Measurement Type*</label>
                  <Select
                    className="measurement-type-select glucose-add-record-type-select"
                    options={measurementTypeOptions.filter(
                      (opt) => opt.value !== "All"
                    )}
                    value={{
                      value: editData.id
                        ? editData.measurement_type
                        : addData.measurement_type,
                      label: editData.id
                        ? editData.measurement_type
                        : addData.measurement_type,
                    }}
                    onChange={handleSelectChange}
                    isSearchable={false}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Date*</label>
                  <input
                    style={{ height: "50px" }}
                    type="date"
                    className={`form-control ${
                      validationErrors.record_date ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_date : addData.record_date
                    }
                    name="record_date"
                    onChange={handleOnChange}
                    max={today}
                    required
                  />
                  {validationErrors.record_date && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.record_date}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Time*</label>
                  <input
                    style={{ height: "50px" }}
                    type="time"
                    className={`form-control mb-0 ${
                      validationErrors.record_time ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_time : addData.record_time
                    }
                    name="record_time"
                    onChange={handleOnChange}
                    required
                  />
                  {validationErrors.record_time && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.record_time}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Add any notes about this measurement"
                    value={editData.id ? editData.notes : addData.notes}
                    name="notes"
                    onChange={handleOnChange}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              type="submit"
              disabled={LoadingEdit || isLoading}
            >
              {LoadingEdit || isLoading
                ? "Saving..."
                : editData.id
                ? "Update"
                : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
      ; ;{/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this blood glucose record? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-default"
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-default"
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default BloodGlucoseList;